import { useApplyTrackProposal, useGraphNodeState, useGraphProposals } from "../advisor/hooks";
import { milestoneMeta } from "./ui";
import type { MilestoneState } from "../types";

// Nazwa stanu w tekście propozycji — z tej samej mapy co reszta aplikacji.
const statePl = (state: string) => milestoneMeta(state as MilestoneState).label;

/** Auto-postęp z FAKTÓW: system wykrył (wypisy zrealizowane, MDCP odebrane, sprawa
 * zamknięta…), że stan kroku jest nieaktualny — proponuje zmianę jednym klikiem.
 * Obok stanów kamieni to samo dla TORU działek (negocjator w terenie, odmowa z
 * zamówienia, sprawa urzędowa, zmiana trasy → przeprojektowanie).
 * Zastosowanie zawsze przez jawną akcję (historia + osoba), nigdy po cichu.
 *
 * Jeden komponent dla schematu blokowego (advisor/ProcessGraphView) i dla tablicy
 * kamieni (MilestonesTab) — te same hooki, te same przyciski, ten sam wygląd; różni
 * się tylko nagłówek i to, czy pokazujemy tor działek (na tablicy kamieni nie ma
 * bloczków działek, więc byłby sygnałem bez miejsca do kliknięcia).
 */
export function FactProposalsBanner({
  connectionId,
  title = "Wykryte fakty — graf wygląda na nieaktualny",
  showTrackProposals = true,
  className = "mb-3",
}: {
  connectionId: string;
  /** Nagłówek paska (bez ⚡ i bez licznika — te dokleja komponent). */
  title?: string;
  /** Tor działek ma sens tylko tam, gdzie widać bloczki działek (graf procesu). */
  showTrackProposals?: boolean;
  className?: string;
}) {
  const { data } = useGraphProposals(connectionId);
  const stateM = useGraphNodeState(connectionId);
  const trackM = useApplyTrackProposal(connectionId);
  const proposals = data?.proposals ?? [];
  const trackProposals = showTrackProposals ? data?.track_proposals ?? [] : [];
  if (proposals.length === 0 && trackProposals.length === 0) return null;
  return (
    <div className={`border border-[#2563eb]/40 bg-[color:rgba(37,99,235,0.05)] rounded p-2 space-y-1 ${className}`}>
      <div className="text-xs font-mono uppercase tracking-wider text-[#2563eb]">
        ⚡ {title} ({proposals.length + trackProposals.length})
      </div>
      {proposals.map((p) => (
        <div key={`${p.node}:${p.proposed_state}`} className="flex flex-wrap items-center gap-2 text-sm">
          <b>{p.label}</b>
          <span className="text-xs text-[var(--color-text-muted)]">
            {statePl(p.current_state)} → <b>{statePl(p.proposed_state)}</b>
          </span>
          <span className="text-xs text-[var(--color-text-secondary)]">{p.reason}</span>
          <button
            type="button"
            className="text-xs font-mono uppercase tracking-wider px-2 py-0.5 border border-[#2563eb] text-[#2563eb] disabled:opacity-50"
            disabled={stateM.isPending}
            onClick={() => stateM.mutate({ node: p.node, state: p.proposed_state, reason: p.reason })}
          >
            ✓ Zastosuj
          </button>
        </div>
      ))}
      {trackProposals.map((p) => (
        <div key={`${p.parcel_id}:${p.element_kind}:${p.action}`} className="flex flex-wrap items-center gap-2 text-sm">
          <b>📍 Działka {p.parcel_ident}</b>
          <span className="text-xs text-[var(--color-text-muted)]">
            {p.label} → <b>{p.action === "fail" ? "nieudany (odmowa)" : "aktywny"}</b>
          </span>
          <span className="text-xs text-[var(--color-text-secondary)]">{p.reason}</span>
          <button
            type="button"
            className="text-xs font-mono uppercase tracking-wider px-2 py-0.5 border border-[#2563eb] text-[#2563eb] disabled:opacity-50"
            disabled={trackM.isPending}
            onClick={() => trackM.mutate({
              parcel_id: p.parcel_id, element_id: p.element_id,
              element_kind: p.element_kind, action: p.action,
            })}
          >
            ✓ Zastosuj
          </button>
        </div>
      ))}
      {stateM.isError && (
        <div className="text-xs text-[var(--color-primary)]">
          Nie udało się zastosować propozycji — spróbuj ponownie albo zmień stan z karty kamienia.
        </div>
      )}
      {trackM.isError && (
        <div className="text-xs text-[var(--color-primary)]">
          Propozycja nieaktualna — fakty się zmieniły, odśwież graf.
        </div>
      )}
    </div>
  );
}
